import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { useEffect } from 'react'
import { authApi, User } from '@/lib/api'

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null

  // Actions
  login: (email: string, password: string) => Promise<void>
  register: (data: {
    email: string
    password: string
    username: string
    displayName: string
    birthDate: string
  }) => Promise<string | undefined>
  loginWithGoogle: (idToken: string) => Promise<void>
  loginWithApple: (idToken: string) => Promise<void>
  sendMagicLink: (email: string, purpose?: 'login' | 'verify_email') => Promise<void>
  verifyMagicLink: (token: string) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
  clearError: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,

      login: async (email: string, password: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.login(email, password)
          const { user, tokens } = response.data
          localStorage.setItem('accessToken', tokens.access_token)
          localStorage.setItem('refreshToken', tokens.refresh_token)
          set({ user, isAuthenticated: true, isLoading: false })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Login failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      register: async (data) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.register(data)
          const { user, tokens, status } = response.data
          if (tokens) {
            localStorage.setItem('accessToken', tokens.access_token)
            localStorage.setItem('refreshToken', tokens.refresh_token)
          }
          // User is registered but not authenticated until email is verified
          set({ user, isAuthenticated: !!tokens, isLoading: false })

          // Return status so caller knows if verification is pending
          return status
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Registration failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      loginWithGoogle: async (idToken: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.loginWithGoogle(idToken)
          const { user, tokens } = response.data
          localStorage.setItem('accessToken', tokens.access_token)
          localStorage.setItem('refreshToken', tokens.refresh_token)
          set({ user, isAuthenticated: true, isLoading: false })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Google login failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      loginWithApple: async (idToken: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.loginWithApple(idToken)
          const { user, tokens } = response.data
          localStorage.setItem('accessToken', tokens.access_token)
          localStorage.setItem('refreshToken', tokens.refresh_token)
          set({ user, isAuthenticated: true, isLoading: false })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Apple login failed'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      sendMagicLink: async (email: string, purpose: 'login' | 'verify_email' = 'login') => {
        set({ isLoading: true, error: null })
        try {
          await authApi.sendMagicLink(email, purpose)
          set({ isLoading: false })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Failed to send magic link'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      verifyMagicLink: async (token: string) => {
        set({ isLoading: true, error: null })
        try {
          const response = await authApi.verifyMagicLink(token)
          const { user, tokens } = response.data
          localStorage.setItem('accessToken', tokens.access_token)
          localStorage.setItem('refreshToken', tokens.refresh_token)
          set({ user, isAuthenticated: true, isLoading: false })
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Invalid magic link'
          set({ error: message, isLoading: false })
          throw error
        }
      },

      logout: async () => {
        try {
          await authApi.logout()
        } catch {
          // Ignore logout errors
        } finally {
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
          set({ user: null, isAuthenticated: false })
        }
      },

      refreshUser: async () => {
        const token = localStorage.getItem('accessToken')
        if (!token) {
          set({ isLoading: false, isAuthenticated: false })
          return
        }

        // Get current persisted state
        const currentState = get()

        // If we already have user data from a previous login, use it
        // This prevents logout on page refresh when /users/me fails temporarily
        if (currentState.user && currentState.isAuthenticated) {
          set({ isLoading: false })
          // Still try to refresh in background, but don't clear state on failure
          try {
            const response = await authApi.me()
            const apiUser = response.data?.user || response.data
            const apiProfile = response.data?.profile as {
              display_name?: string
              bio?: string
              avatar_url?: string
              cover_url?: string
              location?: string
              website?: string
            } | undefined

            const userData: User = {
              ...apiUser,
              displayName: apiProfile?.display_name || apiUser.display_name || apiUser.username,
              bio: apiProfile?.bio,
              avatarUrl: apiProfile?.avatar_url,
              coverUrl: apiProfile?.cover_url,
              location: apiProfile?.location,
              website: apiProfile?.website,
            }
            set({ user: userData })
          } catch {
            // Keep existing user data - let token refresh handle invalid sessions
            console.log('[Auth] /users/me failed, keeping persisted user data')
          }
          return
        }

        // No persisted user data - need to fetch
        try {
          const response = await authApi.me()
          // API returns {user: {...}, profile: {...}} structure
          const apiUser = response.data?.user || response.data
          const apiProfile = response.data?.profile as {
            display_name?: string
            bio?: string
            avatar_url?: string
            cover_url?: string
            location?: string
            website?: string
          } | undefined

          // Merge profile data into user object
          const userData: User = {
            ...apiUser,
            displayName: apiProfile?.display_name || apiUser.display_name || apiUser.username,
            bio: apiProfile?.bio,
            avatarUrl: apiProfile?.avatar_url,
            coverUrl: apiProfile?.cover_url,
            location: apiProfile?.location,
            website: apiProfile?.website,
          }

          set({ user: userData, isAuthenticated: true, isLoading: false })
        } catch {
          localStorage.removeItem('accessToken')
          localStorage.removeItem('refreshToken')
          set({ user: null, isAuthenticated: false, isLoading: false })
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)

// Track if we've initialized
let initialized = false

// Hook for easy access
export function useAuth() {
  const store = useAuthStore()

  // Initialize auth state only once, in useEffect (not during render)
  useEffect(() => {
    if (!initialized) {
      initialized = true
      store.refreshUser()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return store
}

export default useAuth
